import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import Wordmark from './Wordmark';

/**
 * Palbon initialization sequence — the boot / session-verify screen.
 *
 * A central Palbon mark with a ring of business-system nodes and connection
 * lines quietly activating around it, and a short line of status microcopy. Deep
 * midnight, restrained motion, enterprise — not a gaming interface. Respects
 * prefers-reduced-motion (static, no cycling).
 */
const MESSAGES = [
  'Initializing Palbon',
  'Connecting business systems',
  'Finance online',
  'Supply chain online',
  'Operations online',
  'Business intelligence online',
  'Workspace ready',
];

const LoadingSpinner: React.FC<{ label?: string }> = ({ label }) => {
  const reduce = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reduce || label) return;
    const t = setInterval(() => setI((v) => (v + 1) % MESSAGES.length), 620);
    return () => clearInterval(t);
  }, [reduce, label]);

  const R = 120;
  const nodes = useMemo(
    () => Array.from({ length: 8 }).map((_, k) => {
      const a = (k / 8) * Math.PI * 2 - Math.PI / 2;
      return { x: Math.cos(a) * R, y: Math.sin(a) * R };
    }),
    []
  );

  const caption = label || MESSAGES[reduce ? 0 : i];

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 5,
        zIndex: 2000,
        backgroundColor: '#05060f',
        background: [
          'radial-gradient(1000px 560px at 50% 34%, #17275a 0%, rgba(23,39,90,0) 60%)',
          'linear-gradient(180deg, #05060f 0%, #0a1024 55%, #0d1b3e 100%)',
        ].join(', '),
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
          backgroundSize: '54px 54px',
          maskImage: 'radial-gradient(ellipse at 50% 42%, #000 25%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 42%, #000 25%, transparent 78%)',
        },
        '@keyframes nodeOn': { '0%,100%': { opacity: 0.3, r: 2 }, '50%': { opacity: 1, r: 3.4 } },
        '@keyframes lineDraw': { to: { strokeDashoffset: 0 } },
        '@keyframes ringOut': { '0%': { transform: 'scale(0.5)', opacity: 0.7 }, '100%': { transform: 'scale(2.2)', opacity: 0 } },
        '@keyframes coreGlow': { '0%,100%': { boxShadow: '0 0 0 0 rgba(56,189,248,0.0), 0 0 26px 2px rgba(56,189,248,0.18)' }, '50%': { boxShadow: '0 0 0 0 rgba(56,189,248,0.0), 0 0 40px 6px rgba(56,189,248,0.32)' } },
      }}
    >
      {/* Node network */}
      <Box sx={{ position: 'relative', width: 300, height: 300 }}>
        <Box component="svg" viewBox="-150 -150 300 300" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          {/* connection lines */}
          {nodes.map((n, k) => (
            <line
              key={`l${k}`}
              x1={0} y1={0} x2={n.x} y2={n.y}
              stroke="rgba(129,140,248,0.22)" strokeWidth={0.8}
              strokeDasharray={R} strokeDashoffset={reduce ? 0 : R}
              style={reduce ? undefined : { animation: `lineDraw 1s cubic-bezier(.16,1,.3,1) ${0.15 + k * 0.09}s forwards` }}
            />
          ))}
          {/* expanding scan rings */}
          {!reduce && [0, 1].map((k) => (
            <circle key={`r${k}`} cx={0} cy={0} r={30} fill="none" stroke="rgba(56,189,248,0.35)" strokeWidth={0.8}
              style={{ transformOrigin: 'center', animation: `ringOut 3s ease-out ${k * 1.5}s infinite` }} />
          ))}
          {/* nodes */}
          {nodes.map((n, k) => (
            <circle key={`n${k}`} cx={n.x} cy={n.y} r={2.6} fill="#38bdf8"
              style={reduce ? { opacity: 0.9 } : { animation: `nodeOn ${2.6 + (k % 3) * 0.5}s ease-in-out ${0.5 + k * 0.12}s infinite` }} />
          ))}
        </Box>

        {/* center mark */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            width: 92,
            height: 92,
            borderRadius: 4,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)',
            animation: reduce ? 'none' : 'coreGlow 2.4s ease-in-out infinite',
          }}
        >
          <Wordmark size={44} tone="dark" gradient showText={false} />
        </Box>
      </Box>

      {/* status caption */}
      <Box sx={{ textAlign: 'center', minHeight: 44 }}>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '0.02em', color: '#e2e8f0', transition: 'opacity .3s' }}>
          {caption}
          {!/online|ready/i.test(caption) && <Box component="span" sx={{ ml: 0.25, opacity: 0.6 }}>…</Box>}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: '0.6875rem', letterSpacing: '0.32em', color: 'rgba(148,163,184,0.6)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
          PALBON ENTERPRISE PLATFORM
        </Typography>
      </Box>
    </Box>
  );
};

export default LoadingSpinner;
